import { useEffect, useMemo, useState } from "react";
import { epicengageSupabasev1 } from "../../../../../integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../../ui/dialog";
import { Input } from "../../../../ui/input";
import { Button } from "../../../../ui/button";
import { Checkbox } from "../../../../ui/checkbox";
import { Badge } from "../../../../ui/badge";
import { Loader2, Search, Users } from "lucide-react";

type CustomerRow = {
  id: string;
  name: string | null;
  email: string;
  status: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  initialSelected: string[];
  onConfirm: (ids: string[]) => void;
};

export function SelectCustomersDialog({ open, onOpenChange, merchantId, initialSelected, onConfirm }: Props) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected));

  useEffect(() => {
    if (open) setSelected(new Set(initialSelected));
  }, [open, initialSelected]);

  useEffect(() => {
    if (!open || !merchantId) return;
    let active = true;
    setLoading(true);
    (async () => {
      const PAGE = 1000;
      let from = 0;
      const all: CustomerRow[] = [];
      while (active) {
        const { data, error } = await epicengageSupabasev1
          .from("customers")
          .select("id, name, email, status")
          .eq("merchant_id", merchantId)
          .eq("archived", false)
          .is("unsubscribed_at", null)
          .order("name", { ascending: true, nullsFirst: false })
          .range(from, from + PAGE - 1);
        if (error) break;
        const batch = (data as CustomerRow[]) || [];
        all.push(...batch);
        if (batch.length < PAGE) break;
        from += PAGE;
      }
      if (!active) return;
      setRows(all);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [open, merchantId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => (r.name || "").toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((r) => next.delete(r.id));
      else filtered.forEach((r) => next.add(r.id));
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select specific customers</DialogTitle>
          <DialogDescription>
            Hand-pick the customers who should receive this campaign. Unsubscribed and archived customers are excluded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={toggleAllVisible}
              className="text-primary hover:underline disabled:opacity-50"
              disabled={filtered.length === 0}
            >
              {allVisibleSelected ? "Clear visible" : "Select all visible"}
            </button>
            <Badge variant="secondary" className="gap-1">
              <Users className="h-3 w-3" /> {selected.size} selected
            </Badge>
          </div>

          <div className="border rounded-md max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                Loading customers…
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No customers match.</div>
            ) : (
              <ul className="divide-y">
                {filtered.map((c) => {
                  const checked = selected.has(c.id);
                  return (
                    <li key={c.id}>
                      <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent/40">
                        <Checkbox checked={checked} onCheckedChange={() => toggle(c.id)} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{c.name || c.email}</div>
                          {c.name && <div className="text-xs text-muted-foreground truncate">{c.email}</div>}
                        </div>
                        {c.status && (
                          <Badge variant="outline" className="text-xs capitalize">{c.status}</Badge>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => {
              onConfirm(Array.from(selected));
              onOpenChange(false);
            }}
          >
            Use {selected.size} customer{selected.size === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
